import type { WorkOrderTableViewModel } from 'ab-viewModels/workOrderTable.viewModel';

import { noRevenue, shouldUseAutomaticCalculation } from '@acceligentllc/shared/utils/productionData';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

const WORK_ORDER_TABLE_CSV_HEADER = [
	'Work Order',
	'Job title',
	'Date of work',
	'Crew',
	'Crew Type',
	'Superintendent',
	'Project Manager',
	'Status',
	'Revenue',
	'Man Hours',
	'Notes',
	'Equip./Labor/Temp.Labor',
	'Payroll Differential',
];

export const relatedWorkOrdersToCSVData = (viewModels: WorkOrderTableViewModel[]): string[][] => {
	const header: string[] = [...WORK_ORDER_TABLE_CSV_HEADER];
	const rows: string[][] = [];
	let maxEquipmentCount = 0;
	const equipmentData: string[][] = [];
	let maxEmployeeCount = 0;
	const employeesData: string[][] = [];
	let maxTempEmployeeCount = 0;
	const tempEmployeesData: string[][] = [];

	viewModels.forEach((_workOrder) => {
		rows.push([
			`${_workOrder.code}${_workOrder.revision ? `, REV ${_workOrder.revision}` : ''}`,
			_workOrder.jobTitle ? _workOrder.jobTitle : '-',
			`${TimeUtils.getShortDayName(_workOrder.dueDate)}, ${_workOrder.dueDate}`,
			_workOrder.crewNumber.toString(),
			_workOrder.crewType ? _workOrder.crewType : '-',
			_workOrder.supervisor ? _workOrder.supervisor.fullName : '-',
			_workOrder.projectManager ? _workOrder.projectManager.fullName : '-',
			_workOrder.status ? _workOrder.status : '-',
			_revenue(_workOrder),
			_workOrder.manHourAverage! || '-',
			_workOrder.notes ? _workOrder.notes : '-',
			`${_workOrder.workOrderEquipment.length}/${_workOrder.workOrderEmployees.length}/${_workOrder.workOrderTemporaryEmployees.length}`,
			_workOrder.payrollDifferential ? `${_workOrder.payrollDifferential}` : '-',
		]);
		maxEquipmentCount = Math.max(maxEquipmentCount, _workOrder.workOrderEquipment.length);
		equipmentData.push(_workOrder.workOrderEquipment.map(_toEquipmentCSVCell));
		maxEmployeeCount = Math.max(maxEmployeeCount, _workOrder.workOrderEmployees.length);
		employeesData.push(_workOrder.workOrderEmployees.map(_toEmployeeCSVCell));
		maxTempEmployeeCount = Math.max(maxTempEmployeeCount, _workOrder.workOrderTemporaryEmployees.length);
		tempEmployeesData.push(_workOrder.workOrderTemporaryEmployees.map(_toTempEmployeeCSVCell));
	});

	for (let _index = 0; _index < maxEquipmentCount; _index++) {
		header.push(`equipment.${_index}`);
		viewModels.forEach((_workOrder, _woIndex) =>
			rows[_woIndex].push(_toEquipmentCSVCell(_workOrder.workOrderEquipment[_index]))
		);
	}
	for (let _index = 0; _index < maxEmployeeCount; _index++) {
		header.push(`labor.${_index}`);
		viewModels.forEach((_workOrder, _woIndex) =>
			rows[_woIndex].push(_toEmployeeCSVCell(_workOrder.workOrderEmployees[_index]))
		);
	}
	for (let _index = 0; _index < maxTempEmployeeCount; _index++) {
		header.push(`tempLabor.${_index}`);
		viewModels.forEach((_workOrder, _woIndex) =>
			rows[_woIndex].push(_toTempEmployeeCSVCell(_workOrder.workOrderTemporaryEmployees[_index]))
		);
	}

	return [header, ...rows];
};

const _revenue = (workOrder: WorkOrderTableViewModel) => {
	const dueDate = TimeUtils.formatDate(workOrder.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY);

	if (!shouldUseAutomaticCalculation(dueDate)) {
		return workOrder.revenue! || '-';
	}

	const revenueSubtractions = (process.env.FTD_DISABLE_PR_DIFF !== 'true' ? workOrder.payrollDifferential ?? 0 : 0) + +(workOrder.materialCost ?? 0) - +(workOrder.salesMarkup ?? 0);

	return !workOrder.hasOnlyNonBillableWork ? workOrder.revenue ?? '0' : noRevenue({ dueDate, hasOnlyNonBillableWork: !!workOrder.hasOnlyNonBillableWork, revenueSubtractions });
};

/**
	 * Might not be available in scenarios where we're exporting multiple WOs with different number of temporary employees on each
	 */
const _toTempEmployeeCSVCell = (woTempEmployee: WorkOrderTableViewModel['workOrderTemporaryEmployees'][number] | undefined) => {
	return woTempEmployee
		? `${woTempEmployee.fullName} ${woTempEmployee.uniqueId ? ` (${woTempEmployee.uniqueId})` : ''}`
		: '-';
};

/**
 * Might not be available in scenarios where we're exporting multiple WOs with different number of employees on each
 */
const _toEmployeeCSVCell = (woEmployee: WorkOrderTableViewModel['workOrderEmployees'][number] | undefined) => {
	return woEmployee
		? `${woEmployee.fullName} ${woEmployee.uniqueId ? ` (${woEmployee.uniqueId})` : ''}`
		: '-';
};

/**
 * Might not be available in scenarios where we're exporting multiple WOs with different number of equipment on each
 */
const _toEquipmentCSVCell = (woEquipment: WorkOrderTableViewModel['workOrderEquipment'][number] | undefined) => {
	return woEquipment
		? `${woEquipment.code}${woEquipment.specification ? ` (${woEquipment.specification})` : ''}`
		: '-';
};
